import React, { useState, useEffect } from "react";
import { User, Phone, Mail, Building2, Home, RefreshCw, Plus } from "lucide-react";
import BottomSheet from "../shared/BottomSheet";
import FormSelect from "../forms/FormSelect";
import { getChurches, createPastoralIdentity } from "../../lib/api";
import { toast } from "@/lib/toast";

// Sheet compartido para crear el hogar pastoral (registro + familia + anclaje a iglesia).
// Extraído de Profile.jsx para reutilizarse también en el primer arranque (FloatingWizard,
// setup_new) sin duplicar la lógica. Self-contained: trae su propio Field + clases para no
// acoplarse a los helpers locales de Profile.jsx.

function Field({ label, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
        <Icon size={11} className="shrink-0" />
        {label}
      </label>
      {children}
    </div>
  );
}

const INPUT_CLASS =
  "w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/60 " +
  "rounded-[14px] px-4 py-3 text-[15px] text-slate-800 dark:text-slate-100 " +
  "placeholder-slate-400 dark:placeholder-slate-500 " +
  "focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-brand/30 " +
  "focus:border-brand/60 outline-none transition-all duration-200";

const READONLY_CLASS =
  "w-full bg-slate-100/70 dark:bg-slate-800/30 border border-slate-200/50 dark:border-slate-700/30 " +
  "rounded-[14px] px-4 py-3 text-[15px] text-slate-400 dark:text-slate-500 select-none";

export default function CreateIdentitySheet({ isOpen, onClose, district, authEmail, defaultName, defaultPhone, onCreated }) {
  const [form,     setForm]     = useState({ name: "", phone: "", email: "", familyName: "", churchId: "" });
  const [churches, setChurches] = useState([]);
  const [saving,   setSaving]   = useState(false);

  // Pre-fill on each open
  useEffect(() => {
    if (!isOpen) return;
    const lastName = defaultName?.trim().split(/\s+/).pop() || "";
    setForm({
      name:       defaultName  || "",
      phone:      defaultPhone || "",
      email:      authEmail    || "",
      familyName: lastName ? `Familia ${lastName}` : "",
      churchId:   "",
    });
  }, [isOpen, defaultName, defaultPhone, authEmail]);

  useEffect(() => {
    if (!isOpen || !district?.id) return;
    getChurches(district.id)
      .then((data) => setChurches(data || []))
      .catch(() => {});
  }, [isOpen, district?.id]);

  // Auto-update family name while typing the person's name
  const handleNameChange = (val) => {
    setForm((prev) => {
      const lastName     = val.trim().split(/\s+/).pop() || "";
      const prevLastName = prev.name.trim().split(/\s+/).pop() || "";
      const isAutoName   = !prev.familyName || prev.familyName === `Familia ${prevLastName}`;
      return {
        ...prev,
        name:       val,
        familyName: isAutoName ? (lastName ? `Familia ${lastName}` : "") : prev.familyName,
      };
    });
  };

  const ch = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (saving) return;
    if (!form.name.trim())       { toast.error("El nombre es requerido");           return; }
    if (!form.churchId)          { toast.error("Selecciona una iglesia");            return; }
    if (!form.familyName.trim()) { toast.error("El nombre del hogar es requerido"); return; }

    setSaving(true);
    try {
      const result = await createPastoralIdentity({
        familyName: form.familyName,
        personName: form.name,
        phone:      form.phone  || null,
        email:      form.email  || null,
        churchId:   form.churchId,
        districtId: district.id,
      });
      toast.success("Hogar pastoral creado");
      onCreated(result);
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("already exists in this church")) {
        toast.error("Ya existe un hogar con ese nombre en esa iglesia. Cambia el nombre del hogar.");
      } else if (msg.includes("only approved")) {
        toast.error("Solo cuentas aprobadas pueden crear un hogar pastoral.");
      } else if (msg.includes("already has a pastoral identity")) {
        toast.error("Tu perfil ya tiene un hogar pastoral vinculado.");
      } else if (msg.includes("church does not belong")) {
        toast.error("La iglesia seleccionada no pertenece a tu distrito.");
      } else {
        toast.error(msg || "No se pudo crear el hogar pastoral");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      subtitle="Hogar pastoral"
      title="Configurar hogar"
      footer={
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-12 bg-[#1b4f9c] text-white rounded-[16px] text-[15px] font-semibold shadow-[0_4px_12px_rgba(27,79,156,0.3)] transition-all duration-200 ease-out hover:brightness-110 active:scale-[0.98] active:opacity-80 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <RefreshCw size={15} className="animate-spin" /> : <Plus size={16} />}
          {saving ? "Creando…" : "Crear hogar pastoral"}
        </button>
      }
    >
      <div className="space-y-4">
        <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug">
          Se creará tu registro pastoral, tu hogar, y se vincularán a tu cuenta automáticamente.
        </p>

        <Field label="Nombre" icon={User}>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Tu nombre completo"
            className={INPUT_CLASS}
            autoComplete="name"
          />
        </Field>

        <Field label="Teléfono" icon={Phone}>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => ch("phone", e.target.value)}
            placeholder="Número de teléfono"
            className={INPUT_CLASS}
            inputMode="tel"
            autoComplete="tel"
          />
        </Field>

        <Field label="Email pastoral" icon={Mail}>
          <input
            type="email"
            value={form.email}
            onChange={(e) => ch("email", e.target.value)}
            placeholder="Correo de contacto pastoral"
            className={INPUT_CLASS}
            inputMode="email"
            autoComplete="email"
          />
        </Field>

        <Field label="Iglesia" icon={Building2}>
          {churches.length === 0 ? (
            <div className={READONLY_CLASS + " flex items-center gap-2"}>
              <RefreshCw size={13} className="animate-spin text-slate-400 shrink-0" />
              <span>Cargando iglesias…</span>
            </div>
          ) : (
            <FormSelect
              variant="menu"
              value={form.churchId}
              onChange={(v) => ch("churchId", v)}
              options={churches.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Seleccionar iglesia…"
            />
          )}
        </Field>

        <Field label="Nombre del hogar" icon={Home}>
          <input
            type="text"
            value={form.familyName}
            onChange={(e) => ch("familyName", e.target.value)}
            placeholder="Ej: Familia García"
            className={INPUT_CLASS}
          />
          <p className="text-[10px] text-slate-400 dark:text-slate-500">
            Se actualiza automáticamente con tu apellido.
          </p>
        </Field>
      </div>
    </BottomSheet>
  );
}
